import { useState } from "react";
import { ReactNode } from "react";
import styled from "styled-components";
import { useSnackbar } from "notistack";

import { Button, Modal } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu.js";

import { monolithMoment } from "@/utils/date-format";
import { CocRecord } from "../Coc.types";
import EvidenceCocAPI from "@/api/evidence/coc";

interface DeleteCocModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (defaultInfo?: CocRecord) => void;
  onCancel: () => void;
  buttonProps?: {
    cancelText: string;
    submitText: string;
  };
  defaultInfo: CocRecord;
}

const DeleteCocModal: React.FC<DeleteCocModalProps> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
  buttonProps,
  defaultInfo,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await EvidenceCocAPI.deleteCocRecord(defaultInfo.coc_id, defaultInfo);
      enqueueSnackbar(`Chain of Custody Record Deleted.`, {
        variant: "success",
      });
      onSubmit?.(defaultInfo);
    } catch (error) {
      console.error("Error deleting CoC record:", error);
      enqueueSnackbar(`Error deleting Chain of Custody record.`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
      onClose?.();
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  return (
    <Modal open={show} onClose={onClose}>
      <Modal.Title>Delete Chain of Custody Record</Modal.Title>
      <div style={{ margin: "10px 0px" }}>
        Are you sure you want to delete this Chain of Custody Record?
      </div>
      <div style={{ margin: "20px 0px" }}>
        <DetailItem label="Type" value={defaultInfo.type} />
        <DetailItem
          label="Timestamp"
          value={monolithMoment({
            timestamp: defaultInfo.timestamp,
            includeTime: true,
            includeZone: true,
          })}
        />
        <DetailItem
          label="Custody From"
          value={
            <div>
              <div>{defaultInfo.custody_from}</div>
            </div>
          }
        />
        <DetailItem
          label="Custody To"
          value={
            <div>
              <div>{defaultInfo.custody_to}</div>
            </div>
          }
        />
        <DetailItem label="Description" value={defaultInfo.reason} />
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="error"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          {buttonProps?.submitText || "Delete Record"}
        </Button>
      </ButtonMenu>
    </Modal>
  );
};

const DetailLabel = styled.div`
  font-weight: 500;
  width: 125px;
  min-width: 125px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const DetailValue = styled.div`
  white-space: pre-wrap;
`;

interface DetailItemProps {
  className?: string;
  label: string;
  value: ReactNode;
  labelProps?: Record<string, unknown>;
  valueProps?: Record<string, unknown>;
}

const DetailItem = styled<React.FC<DetailItemProps>>(
  ({ className, label, value, labelProps, valueProps, ...props }) => {
    return (
      <div className={className + " detail-item"} {...props}>
        <DetailLabel className={"detail-label"} {...labelProps}>
          {label}
        </DetailLabel>
        <DetailValue className={"detail-value"} {...valueProps}>
          {value}
        </DetailValue>
      </div>
    );
  }
)`
  display: flex;
  flex-direction: row;
  gap: 5px;
  margin-bottom: 5px;
`;

export default DeleteCocModal;
